/**
 * Compact popover for editing a transition (playlist item OR playlist-group
 * assignment). Keeps the row layout uncluttered — the row shows a small
 * icon; clicking it opens this. Local draft is committed via `onSave` when
 * the popover closes (Done / click-away).
 *
 * `mode="item"` exposes Transition In + Out and a "Use playlist default"
 * switch. `mode="group"` exposes a single "Transition to next playlist"
 * and a "Use group default" switch.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Divider, FormControlLabel, ListSubheader, MenuItem,
  Popover, Select, Stack, Switch, TextField, Typography,
} from "@mui/material";
import {
  DEFAULT_DURATION_MS, DEFAULT_EASING, DURATION_PRESETS_SECONDS,
  DURATION_RANGE_MESSAGE, EASINGS, GROUP_TRANSITIONS, MAX_DURATION_MS,
  MIN_DURATION_MS, TRANSITIONS, TransitionOption,
} from "../../Player/transitions";
import { buildTransitionPlan } from "../../Player/transitionEngine";
import { TransitionStage } from "../../Player/TransitionStage";

export interface TransitionDraft {
  transition_in?: string | null;
  transition_out?: string | null;
  transition_duration_ms?: number | null;
  transition_easing?: string | null;
  use_default?: boolean;
}

const GROUPS: TransitionOption["group"][] = ["Basic", "Directional", "Zoom", "Advanced"];

function groupedMenuItems(options: TransitionOption[]) {
  return GROUPS.flatMap((g) => {
    const items = options.filter((o) => o.group === g);
    if (items.length === 0) return [];
    return [
      <ListSubheader key={`h-${g}`} sx={{ lineHeight: "28px" }}>{g}</ListSubheader>,
      ...items.map((o) => (
        <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
      )),
    ];
  });
}

/**
 * Mini looped preview using the exact device engine. When `nodeA`/`nodeB` are
 * supplied (item mode from the playlist editor) it loops the REAL selected
 * item → next item (spec §7); otherwise it falls back to an abstract A→B card
 * (group mode / no media context).
 */
function MiniPreview({
  id, easing, durationMs, nodeA, nodeB,
}: {
  id: string; easing: string; durationMs: number;
  nodeA?: React.ReactNode; nodeB?: React.ReactNode;
}) {
  const [key, setKey] = useState(0);
  const plan = useMemo(
    () => buildTransitionPlan(id, easing, durationMs),
    [id, easing, durationMs],
  );
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    timer.current = window.setInterval(() => setKey((k) => k + 1), Math.max(1400, durationMs + 1100));
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [durationMs]);

  const even = key % 2 === 0;
  const hasReal = nodeA !== undefined && nodeB !== undefined;
  const scene = hasReal ? (
    <Box sx={{ width: "100%", height: "100%" }}>{even ? nodeA : nodeB}</Box>
  ) : (
    <Box sx={{
      width: "100%", height: "100%", display: "flex", alignItems: "center",
      justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 28,
      background: even
        ? "linear-gradient(135deg,#6366f1,#8b5cf6)"
        : "linear-gradient(135deg,#0ea5e9,#22c55e)",
    }}>
      {even ? "A" : "B"}
    </Box>
  );

  return (
    <Box sx={{ position: "relative", width: "100%", height: 120, borderRadius: 1.5, overflow: "hidden", background: "#000" }}>
      <TransitionStage sceneKey={key} plan={plan}>{scene}</TransitionStage>
    </Box>
  );
}

export default function TransitionEditorPopover({
  open, anchorEl, onClose, value, onSave, mode, defaultLabel,
  previewNodes, itemDurationSeconds,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  value: TransitionDraft;
  onSave: (draft: TransitionDraft) => void;
  mode: "item" | "group";
  defaultLabel: string;
  /** Real selected-item → next-item scenes for the live preview (spec §7). */
  previewNodes?: { current: React.ReactNode; next: React.ReactNode };
  /** The item's configured display duration in seconds, for the §8 guard.
   *  Undefined for "plays to end" videos / group mode (validation skipped). */
  itemDurationSeconds?: number;
}) {
  const options = mode === "group" ? GROUP_TRANSITIONS : TRANSITIONS;
  const [draft, setDraft] = useState<TransitionDraft>(value);
  const [customSecs, setCustomSecs] = useState<string>("");
  const [showPreview, setShowPreview] = useState(false);

  // Re-seed the draft whenever the popover (re)opens for a new target.
  useEffect(() => {
    if (open) {
      setDraft(value);
      setCustomSecs("");
      setShowPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const useDefault = draft.use_default ?? true;
  // The draft always uses `transition_in` as the primary id; the parent
  // maps it to transition_in (item) or transition_to_next (group).
  const primaryId = draft.transition_in || "cut";
  const durationMs = draft.transition_duration_ms ?? DEFAULT_DURATION_MS;
  const easing = draft.transition_easing ?? DEFAULT_EASING;
  const durationSecs = +(durationMs / 1000).toFixed(3);
  const isPreset = DURATION_PRESETS_SECONDS.includes(durationSecs);

  const customError = useMemo(() => {
    if (customSecs === "") return "";
    const n = Number(customSecs);
    if (Number.isNaN(n) || n < MIN_DURATION_MS / 1000 || n > MAX_DURATION_MS / 1000) {
      return DURATION_RANGE_MESSAGE;
    }
    return "";
  }, [customSecs]);

  // Spec §8: a transition must be shorter than the item it plays over. Only
  // meaningful for an animated transition on an item with a known duration.
  const itemDurationError =
    !useDefault &&
    primaryId !== "cut" && primaryId !== "none" &&
    itemDurationSeconds !== undefined &&
    durationMs >= itemDurationSeconds * 1000
      ? "Transition duration must be shorter than the item duration."
      : "";

  const patch = (p: Partial<TransitionDraft>) => setDraft((d) => ({ ...d, ...p }));
  const setPrimary = (id: string) => patch({ transition_in: id });

  const commit = () => { onSave(draft); onClose(); };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={commit}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { width: 320, p: 2 } }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {mode === "group" ? "Transition to next playlist" : "Item transition"}
      </Typography>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={useDefault}
            onChange={(e) => patch({ use_default: e.target.checked })}
          />
        }
        label={<Typography variant="body2">{defaultLabel}</Typography>}
        sx={{ mb: 1 }}
      />

      <Stack spacing={1.5} sx={{ opacity: useDefault ? 0.5 : 1, pointerEvents: useDefault ? "none" : "auto" }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {mode === "group" ? "Transition" : "Transition In"}
          </Typography>
          <Select
            fullWidth size="small" value={primaryId}
            onChange={(e) => setPrimary(String(e.target.value))}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          >
            {groupedMenuItems(options)}
          </Select>
        </Box>

        {mode === "item" && (
          <Box>
            <Typography variant="caption" color="text.secondary">Transition Out (optional)</Typography>
            <Select
              fullWidth size="small" value={draft.transition_out || "none"}
              onChange={(e) => patch({ transition_out: String(e.target.value) })}
              MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            >
              {groupedMenuItems(options)}
            </Select>
          </Box>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary">Duration</Typography>
          <Select
            fullWidth size="small"
            value={isPreset ? String(durationSecs) : "custom"}
            onChange={(e) => {
              const v = String(e.target.value);
              if (v === "custom") { setCustomSecs(durationSecs.toString()); return; }
              setCustomSecs("");
              patch({ transition_duration_ms: Math.round(Number(v) * 1000) });
            }}
          >
            {DURATION_PRESETS_SECONDS.map((s) => (
              <MenuItem key={s} value={String(s)}>{s} s</MenuItem>
            ))}
            <MenuItem value="custom">Custom…</MenuItem>
          </Select>
          {(!isPreset || customSecs !== "") && (
            <TextField
              fullWidth size="small" type="number" sx={{ mt: 1 }}
              label="Custom seconds (0.1–5)"
              value={customSecs}
              error={!!customError}
              helperText={customError || " "}
              inputProps={{ step: 0.1, min: 0.1, max: 5 }}
              onChange={(e) => {
                setCustomSecs(e.target.value);
                const n = Number(e.target.value);
                if (!Number.isNaN(n) && n >= MIN_DURATION_MS / 1000 && n <= MAX_DURATION_MS / 1000) {
                  patch({ transition_duration_ms: Math.round(n * 1000) });
                }
              }}
            />
          )}
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">Easing</Typography>
          <Select
            fullWidth size="small" value={easing}
            onChange={(e) => patch({ transition_easing: String(e.target.value) })}
          >
            {EASINGS.map((e) => (
              <MenuItem key={e.id} value={e.id}>{e.label}</MenuItem>
            ))}
          </Select>
        </Box>

        {itemDurationError && (
          <Typography variant="caption" sx={{ color: "error.main" }}>
            {itemDurationError}
          </Typography>
        )}

        <Button size="small" variant="text" onClick={() => setShowPreview((s) => !s)}>
          {showPreview ? "Hide preview" : "Preview transition"}
        </Button>
        {showPreview && (
          <MiniPreview
            id={primaryId} easing={easing} durationMs={durationMs}
            nodeA={previewNodes?.current} nodeB={previewNodes?.next}
          />
        )}
      </Stack>

      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" justifyContent="flex-end">
        <Button size="small" variant="contained" onClick={commit}>Done</Button>
      </Stack>
    </Popover>
  );
}
